import { axiosInstanceV2 } from '../axios';

export async function fetchSubscriptionPlans(params = {}) {
	try {
		const token = localStorage.getItem('authToken');

		const { data } = await axiosInstanceV2.get(`/plans/`, {
			params,
			headers: {
				Authorization: `Bearer ${token}`,
			},
		});

		return data?.data;
	} catch (error) {
		throw error;
	}
}

export async function proceedToCheckout(payload) {
	try {
		const token = localStorage.getItem('authToken');

		const { data } = await axiosInstanceV2.post(`/subscription/proceed-to-checkout`, payload, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${token}`,
			},
		});

		return data;
	} catch (error) {
		throw error;
	}
}

export async function testDowngradeToFree() {
	const token = localStorage.getItem('authToken');
	const { data } = await axiosInstanceV2.get('/worker/test-downgrade-to-free', {
		headers: {
			Authorization: `Bearer ${token}`,
		},
	});
	return data;
}

/**
 * Runs a scheduled downgrade cutover immediately.
 *
 * Points at /test-process-downgrade, not the older
 * /test-downgrade-subscription. That endpoint only ENQUEUES the BullMQ job, so
 * it silently did nothing unless a separate `dev:worker` process happened to be
 * running to consume the queue — which looked exactly like the downgrade being
 * broken. This one executes the same handler synchronously and returns the
 * result.
 */
export async function testDowngradeSubscription() {
	const token = localStorage.getItem('authToken');

	const { data } = await axiosInstanceV2.get('/worker/test-process-downgrade', {
		headers: {
			Authorization: `Bearer ${token}`,
		},
	});

	return data;
}

/**
 * Expires a NON-RECURRING (one-time) subscription immediately.
 *
 * Neither of the other testers can reach this state: testDowngradeSubscription
 * runs a cutover onto a next plan, which a one-time subscription doesn't have,
 * and testFinalizeCancellation needs `cancel_at_cycle_end` plus an `active`
 * status — a one-time subscription has neither, because nothing was cancelled
 * and Razorpay marks it `completed` once its single charge succeeds.
 *
 * This is the only way to prove what a non-recurring downgrade actually does
 * at expiry: the organisation moves to Free, and the plan the customer chose
 * is kept as their selected next plan rather than being applied.
 */
/**
 * Raises a subscription usage alert on demand, so the dashboard banner and the
 * notification dropdown can be tested without consuming a plan's worth of seats
 * or waiting for the hourly scheduler.
 *
 * `count > 1` deliberately writes DUPLICATE rows for the same condition — the
 * shape the old scheduler produced — to prove the collapse holds: the banner
 * should still show one, and dismissing it should retire the whole group.
 */
export async function testUsageAlert({ feature = 'users', threshold = 100, count = 1 } = {}) {
	const token = localStorage.getItem('authToken');

	const { data } = await axiosInstanceV2.get('/worker/test-usage-alert', {
		params: { feature, threshold, count },
		headers: {
			Authorization: `Bearer ${token}`,
		},
	});

	return data;
}

export async function testExpireNonRecurring() {
	const token = localStorage.getItem('authToken');

	const { data } = await axiosInstanceV2.get('/worker/test-expire-non-recurring', {
		headers: {
			Authorization: `Bearer ${token}`,
		},
	});

	return data;
}

export async function testFinalizeCancellation() {
	const token = localStorage.getItem('authToken');

	const { data } = await axiosInstanceV2.get('/worker/test-finalize-cancellation', {
		headers: {
			Authorization: `Bearer ${token}`,
		},
	});

	return data;
}

// Schedules or immediately cancels the subscription. cancel_at_cycle_end=1
// (default) never touches Razorpay — it flags the row and schedules the
// finalize_cancellation job for current_end. cancel_at_cycle_end=0 cancels now.
export async function cancelSubscription({ cancel_at_cycle_end = 1, reasons = [] } = {}) {
	const token = localStorage.getItem('authToken');

	const { data } = await axiosInstanceV2.patch(
		'/subscription/cancel',
		{ cancel_at_cycle_end, reasons },
		{ headers: { Authorization: `Bearer ${token}` } },
	);

	return data;
}

// Confirms a just-completed payment against Razorpay and applies the result
// immediately, instead of waiting for the asynchronous webhook. Without this
// the app redirects before activation lands and shows the previous plan.
//
// Returns { status, razorpay_status, is_settled } — is_settled is false while
// the provider is still confirming, which the success page uses to poll.
export async function syncSubscription({ razorpay_subscription_id }) {
	const token = localStorage.getItem('authToken');

	const { data } = await axiosInstanceV2.post(
		'/subscription/sync',
		{ razorpay_subscription_id },
		{ headers: { Authorization: `Bearer ${token}` } },
	);

	return data?.data;
}

// Everything the Checkout → Billing Details form should open pre-filled with,
// merged server-side from the signup profile, organisation setup and any
// billing details saved on a previous checkout. Read-only — fetching it never
// alters the signup or organisation records.
export async function fetchBillingPrefill() {
	const token = localStorage.getItem('authToken');

	const { data } = await axiosInstanceV2.get('/subscription/billing-prefill', {
		headers: { Authorization: `Bearer ${token}` },
	});

	return data?.data;
}

// Read-only dry run of a plan change, for the confirmation screen the PRD
// requires: current plan, target plan, what's actually charged now (the
// prorated amount for an upgrade), and when it takes effect. Makes no Razorpay
// call and writes nothing, so it is safe to fire as soon as a plan is picked.
export async function previewPlanChange({ razorpay_plan_id, billing_period }) {
	const token = localStorage.getItem('authToken');

	const { data } = await axiosInstanceV2.post(
		'/subscription/change-plan/preview',
		{ razorpay_plan_id, billing_period },
		{ headers: { Authorization: `Bearer ${token}` } },
	);

	return data?.data;
}

// The customer closed the Razorpay window without paying. Retires the unpaid
// subscription checkout created for it, so the app does not show "Payment
// Pending" for a plan they decided not to buy. Returns { abandoned, reason }.
export async function abandonCheckout({ razorpay_subscription_id }) {
	const token = localStorage.getItem('authToken');

	const { data } = await axiosInstanceV2.post(
		'/subscription/abandon-checkout',
		{ razorpay_subscription_id },
		{ headers: { Authorization: `Bearer ${token}` } },
	);

	return data?.data;
}

// Counterpart to syncSubscription for a non-recurring upgrade's one-time
// Invoice/Order payment. There is no subscription webhook for these, so this
// call is what actually applies the plan change — not just a display refresh.
export async function confirmUpgradePayment({
	razorpay_order_id,
	razorpay_payment_id,
	razorpay_signature,
	razorpay_invoice_receipt,
	razorpay_invoice_status,
	invoice_id,
}) {
	const token = localStorage.getItem('authToken');

	const { data } = await axiosInstanceV2.post(
		'/subscription/confirm-upgrade-payment',
		{
			razorpay_order_id,
			razorpay_payment_id,
			razorpay_signature,
			razorpay_invoice_receipt,
			razorpay_invoice_status,
			invoice_id,
		},
		{ headers: { Authorization: `Bearer ${token}` } },
	);

	return data?.data;
}

// Undoes a pending "cancel at cycle end" — never touched Razorpay, so this
// just removes the scheduled job. Subscription keeps renewing normally.
export async function undoScheduledCancellation() {
	const token = localStorage.getItem('authToken');

	const { data } = await axiosInstanceV2.post(
		'/subscription/undo-scheduled-cancellation',
		{},
		{ headers: { Authorization: `Bearer ${token}` } },
	);

	return data;
}
